import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchPromotions,
  createPromotion,
  updatePromotion,
  deletePromotion,
  fetchSubscriptionPlans,
} from "@/api/subscription-plans";
import { fetchUser } from "@/api/users";
import { useNavigate } from "react-router-dom";
import AdminLayout from "@/components/layout/AdminLayout";
import { useToast } from "@/hooks/use-toast";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  PlusCircle,
  Tag,
  Edit,
  Calendar,
  Trash2,
  Plus,
  Search,
  Users,
} from "lucide-react";
import { PromoDiscount, User } from "@/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const PromotionsList = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [promoToDelete, setPromoToDelete] = useState<string | number | null>(
    null
  );
  const [searchQuery, setSearchQuery] = useState("");

  const { data: promotions = [], isLoading: isLoadingPromos } = useQuery({
    queryKey: ["activePromoDiscounts"],
    queryFn: fetchPromotions,
  });

  const togglePromoMutation = useMutation({
    mutationFn: (id: number) =>
      updatePromotion(id, {
        isActive: !promotions.find((p) => p.id === id)?.isActive,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["activePromoDiscounts"] });
      toast({
        title: "Promotion status updated",
        description: "The promotion status has been updated successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deletePromoMutation = useMutation({
    mutationFn: deletePromotion,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["activePromoDiscounts"] });
      toast({
        title: "Promotion deleted",
        description: "The promotion has been deleted successfully.",
      });
      setIsDeleteDialogOpen(false);
      setPromoToDelete(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleTogglePromoActive = (id: string | number) => {
    togglePromoMutation.mutate(Number(id));
  };

  const handleEditPromo = (id: string | number) => {
    navigate(`/admin/promotions/${id}`);
  };

  const handleCreatePromo = () => {
    navigate("/admin/promotions/new");
  };

  const handleDeletePromo = (id: string | number) => {
    setPromoToDelete(id);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeletePromo = () => {
    if (promoToDelete) {
      deletePromoMutation.mutate(Number(promoToDelete));
    }
  };

  // Helper function to get end of day for a date
  const getEndOfDay = (date: Date) => {
    return new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      23,
      59,
      59,
      999
    );
  };

  // Helper function to reset time part of a date
  const getStartOfDay = (date: Date) => {
    return new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      0,
      0,
      0,
      0
    );
  };

  // Filter promotions by search query
  const filterPromotions = (promos) => {
    if (!searchQuery) return promos;

    return promos.filter(
      (promo) =>
        promo.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        promo.promoCode?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        promo.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  if (isLoadingPromos) {
    return (
      <AdminLayout title="Promotions">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      </AdminLayout>
    );
  }

  const now = new Date();
  const today = getStartOfDay(now);

  // Filter active promotions
  const activePromos = filterPromotions(
    promotions.filter(
      (p) =>
        p.isActive &&
        new Date(p.startDate) <= now &&
        getEndOfDay(new Date(p.endDate)) >= now
    )
  );

  // Filter upcoming promotions
  const upcomingPromos = filterPromotions(
    promotions.filter((p) => p.isActive && new Date(p.startDate) > now)
  );

  // Filter expired promotions
  const expiredPromos = filterPromotions(
    promotions.filter(
      (p) => !p.isActive || getEndOfDay(new Date(p.endDate)) < now
    )
  );

  return (
    <AdminLayout title="Promotions">
      <div className="mb-4 flex justify-between items-center">
        <p className="text-muted-foreground text-sm">
          Manage time-limited promotional discounts for subscription plans
        </p>
      </div>

      {/* Search and filter bar */}
      <div className="relative mb-4">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search promotions by name or code..."
          className="pl-9 h-9"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Delete confirmation dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader className="space-y-1">
            <DialogTitle>Delete Promotion</DialogTitle>
            <DialogDescription className="text-xs">
              Are you sure you want to delete this promotion? This action cannot
              be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-1 sm:gap-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={confirmDeletePromo}
              disabled={deletePromoMutation.isPending}
            >
              {deletePromoMutation.isPending ? (
                <>
                  <span className="mr-2">Deleting...</span>
                  <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full"></span>
                </>
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tabs for promotion status */}
      <Tabs defaultValue="active" className="w-full">
        <TabsList className="w-full grid grid-cols-3 h-10">
          <TabsTrigger value="active" className="text-sm">
            Active
            <Badge className="ml-2 bg-green-100 text-green-700 border-none">
              {activePromos.length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="upcoming" className="text-sm">
            Upcoming
            <Badge className="ml-2 bg-blue-100 text-blue-700 border-none">
              {upcomingPromos.length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="expired" className="text-sm">
            Expired
            <Badge className="ml-2 bg-gray-100 text-gray-700 border-none">
              {expiredPromos.length}
            </Badge>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="mt-4">
          {activePromos.length === 0 ? (
            <p className="text-muted-foreground text-center py-6 text-sm">
              {searchQuery
                ? "No matching active promotions found."
                : "No active promotions found."}
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {activePromos.map((promo) => (
                <PromoCard
                  key={promo.id}
                  promo={promo}
                  onToggleActive={handleTogglePromoActive}
                  onEdit={handleEditPromo}
                  onDelete={handleDeletePromo}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="upcoming" className="mt-4">
          {upcomingPromos.length === 0 ? (
            <p className="text-muted-foreground text-center py-6 text-sm">
              {searchQuery
                ? "No matching upcoming promotions found."
                : "No upcoming promotions found."}
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {upcomingPromos.map((promo) => (
                <PromoCard
                  key={promo.id}
                  promo={promo}
                  onToggleActive={handleTogglePromoActive}
                  onEdit={handleEditPromo}
                  onDelete={handleDeletePromo}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="expired" className="mt-4">
          {expiredPromos.length === 0 ? (
            <p className="text-muted-foreground text-center py-6 text-sm">
              {searchQuery
                ? "No matching expired promotions found."
                : "No expired promotions found."}
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {expiredPromos.map((promo) => (
                <PromoCard
                  key={promo.id}
                  promo={promo}
                  onToggleActive={handleTogglePromoActive}
                  onEdit={handleEditPromo}
                  onDelete={handleDeletePromo}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
};

type PromoCardProps = {
  promo: PromoDiscount;
  onToggleActive: (id: string | number) => void;
  onEdit: (id: string | number) => void;
  onDelete: (id: string | number) => void;
};

const PromoCard = ({
  promo,
  onToggleActive,
  onEdit,
  onDelete,
}: PromoCardProps) => {
  const { maxUses } = promo;
  const now = new Date();
  const startDate = new Date(promo.startDate);
  const endDate = new Date(promo.endDate);
  const [collaborator, setCollaborator] = useState<User | null>(null);
  const [isLoadingCollaborator, setIsLoadingCollaborator] = useState(false);

  // Fetch collaborator details if collaboratorId exists
  useEffect(() => {
    const fetchCollaboratorDetails = async () => {
      if (!promo.collaboratorId) return;

      try {
        setIsLoadingCollaborator(true);
        const userData = await fetchUser(promo.collaboratorId);
        setCollaborator(userData);
      } catch (error) {
        console.error("Error fetching collaborator details:", error);
      } finally {
        setIsLoadingCollaborator(false);
      }
    };

    fetchCollaboratorDetails();
  }, [promo.collaboratorId]);

  // Reset the time part of now to compare only dates
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  // Set endDate time to end of day to make the promotion valid for the entire end date
  const endOfDay = new Date(
    endDate.getFullYear(),
    endDate.getMonth(),
    endDate.getDate(),
    23,
    59,
    59,
    999
  );

  let status: "active" | "upcoming" | "expired" = "active";
  if (!promo.isActive) {
    status = "expired";
  } else if (startDate > today) {
    status = "upcoming";
  } else if (endOfDay < now) {
    status = "expired";
  }

  // Format dates as dd/mm/yyyy
  const formatDate = (date: Date): string => {
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Format commission info
  const getCommissionInfo = () => {
    if (!promo.collaboratorId || !promo.collaboratorCommissionValue)
      return null;

    return promo.collaboratorCommissionType === "percentage"
      ? `${promo.collaboratorCommissionValue}% commission`
      : `₹${promo.collaboratorCommissionValue.toFixed(2)} commission`;
  };

  // Get initials for avatar
  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((part) => part[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <Card
      className={`${
        status === "expired" ? "opacity-70" : ""
      } overflow-hidden border shadow-sm flex flex-col h-full`}
    >
      <CardHeader className="p-4 pb-3">
        <div className="flex items-center justify-between">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-200 border-none">
              {promo.discountType === "percentage"
                ? `${promo.discountValue}% off`
                : `₹${promo.discountValue.toFixed(2)} off`}
            </Badge>
            {promo.promoCode && (
              <Badge variant="outline">{promo.promoCode}</Badge>
            )}
          </div>
          <Badge
            variant="outline"
            className={
              status === "active"
                ? "bg-green-100 text-green-700 border-green-200"
                : status === "upcoming"
                ? "bg-blue-100 text-blue-700 border-blue-200"
                : "bg-gray-100 text-gray-700 border-gray-200"
            }
          >
            {status === "active"
              ? "Active"
              : status === "upcoming"
              ? "Upcoming"
              : "Expired"}
          </Badge>
        </div>
        <CardTitle className="text-base font-medium flex items-center gap-1">
          {promo.name}
          {promo.collaboratorId && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge className="ml-1 bg-purple-100 text-purple-700 border-none text-xs">
                    <Users className="h-3 w-3 mr-1" />
                    Collab
                  </Badge>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {isLoadingCollaborator
                    ? "Loading collaborator..."
                    : collaborator
                    ? `Collaborator: ${collaborator.name}`
                    : "Collaborator promotion"}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </CardTitle>
        {promo.description && (
          <CardDescription className="text-sm mt-1 line-clamp-2">
            {promo.description}
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="p-4 pt-0 flex-grow">
        <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
          <span className="text-muted-foreground">Valid from:</span>
          <span className="text-right font-medium">
            {formatDate(startDate)}
          </span>

          <span className="text-muted-foreground">Valid until:</span>
          <span className="text-right font-medium">{formatDate(endDate)}</span>

          <span className="text-muted-foreground">Applicable to:</span>
          <span className="text-right font-medium">
            {!Array.isArray(promo.applicablePlans) ||
            promo.applicablePlans.length === 0
              ? "All plans"
              : "Selected plans"}
          </span>

          <span className="text-muted-foreground">Max uses:</span>
          <span className="text-right font-medium">
            {maxUses === null || maxUses === undefined || maxUses === 0
              ? "Unlimited"
              : `${maxUses} uses`}
          </span>

          {promo.collaboratorId && collaborator && (
            <>
              <span className="text-muted-foreground">Collaborator:</span>
              <span className="text-right flex items-center justify-end gap-1.5 font-medium">
                <Avatar className="h-5 w-5">
                  <AvatarImage
                    src={collaborator.avatarUrl}
                    alt={collaborator.name}
                  />
                  <AvatarFallback className="text-[10px]">
                    {getInitials(collaborator.name)}
                  </AvatarFallback>
                </Avatar>
                <span className="truncate max-w-[100px]">
                  {collaborator.name}
                </span>
              </span>

              {getCommissionInfo() && (
                <>
                  <span className="text-muted-foreground">Commission:</span>
                  <span className="text-right text-purple-600 font-medium">
                    {getCommissionInfo()}
                  </span>
                </>
              )}
            </>
          )}
        </div>
      </CardContent>
      <CardFooter className="p-3 flex justify-end gap-2 bg-muted/10 border-t">
        <Button
          variant="ghost"
          size="sm"
          className="h-8 px-3"
          onClick={() => onToggleActive(promo.id)}
        >
          {promo.isActive ? "Deactivate" : "Activate"}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => onEdit(promo.id)}
        >
          <Edit className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
          onClick={() => onDelete(promo.id)}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </CardFooter>
    </Card>
  );
};

export default PromotionsList;
